import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import bgImage from "../../assets/images/Logo.png";
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import Logo from '../../assets/images/Logo.png'

interface Errors {
    username?: string;
    password?: string;
}

const Login: React.FC = () => {
    const navigate = useNavigate();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [errors, setErrors] = useState<Errors>({});
    const [showPassword, setShowPassword] = useState(false);

    const togglePassword = () => setShowPassword((s) => !s);
    const { login, loading: authLoading } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const newErrors: Errors = {};

        if (!username.trim()) newErrors.username = "Please enter your username.";
        if (!password.trim()) newErrors.password = "Please enter your password.";

        setErrors(newErrors);
        if (Object.keys(newErrors).length > 0) return;

        const res = await login(username, password);

        if (res.success) {
            toast.success("Login successful");
            const user = res.data?.user ?? res.data;

            // If user role is customer, send them to their details (CustomerDetails will read profile id from auth)
            if (user?.role === "Customer" || user?.role === "customer") {
                navigate("/customers/details", { replace: true });
                return;
            }
            navigate("/dashboard", { replace: true });
        } else {
            toast.error(res.message || "Login failed");

            // If backend provides field-specific error, you can set accordingly:
            // setErrors({ username: "Invalid username", password: "Invalid password" });
        }
    };

    return (
        <div className="min-h-screen flex flex-col w-full bg-gray-100 md:bg-white">
            <img src="." alt="" />
            {/* Header */}
            <div className='flex items-center justify-between md:justify-between tracking-widest bg-white block md:border-b md:border-gray-100'>
                <p className='text-2xl font-bold p-5 flex items-center gap-3'>
                    <span className='text-yellow-400'>
                       <div className='w-10 h-10'>
                         <img src={Logo} alt="" />
                       </div>
                        </span> Sri Ambigai 
                </p>
                {/* <button className='py-1 px-3 mr-2 text-md font-semibold rounded-xl text-white shadow-xl bg-yellow-400 border-2 border-yellow-200 relative font-bold text-base cursor-pointer transition-all duration-300 hover:bg-yellow-500 disabled:opacity-60'>
                    Get Started
                </button> */}
            </div>

            {/* Main */}
            <main className="flex-grow flex items-center justify-center">
                <div className="w-full bg-white rounded-lg md:mx-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 min-h-[520px] md:px-3">
                        {/* Left image */}
                        <div className="hidden md:block">
                            <div className="h-full w-full flex items-center justify-center">
                                <div className='text-center p-10'>
                                    <p className='text-lg mb-4 font-medium italic'>Personal Loans & Others</p>
                                    <img src={bgImage} alt="decorative" className="w-100 h-90" />
                                </div>
                            </div>
                        </div>

                        {/* Right form */}
                        <div className="flex items-center justify-center p-8 sm:p-12 bg-gray-100 md:rounded-3xl md:mt-0">
                            <div className="w-full max-w-md">
                                <h2 className="text-2xl font-semibold text-[#00aef0] text-center">Welcome !</h2>
                                <p className="mt-2 text-sm text-gray-500 text-center">
                                    Start Managing Your Finance Faster and Better
                                </p>

                                <form className="mt-6" onSubmit={handleSubmit} noValidate>
                                    {/* Username Field */}
                                    <label className="block">
                                        <span className="text-sm text-gray-600">Username</span>
                                        <input
                                            type="text"
                                            value={username}
                                            onChange={(e) => setUsername(e.target.value)}
                                            className={`mt-1 block w-full rounded-md border px-3 py-2 shadow-sm placeholder-gray-400 bg-white 
                                                 focus:outline-none focus:ring-2 focus:ring-yellow-300
                                                 ${errors.username ? 'border-red-500 focus:ring-red-300' : 'border-gray-200'}`}
                                            placeholder="username"
                                            required
                                            aria-invalid={!!errors.username}
                                        />
                                    </label>
                                    <div className="h-5 mt-1">
                                        <p
                                            className={`text-sm text-red-600 transition-opacity duration-200 ${errors.username ? 'opacity-100' : 'opacity-0'
                                                }`}
                                        >
                                            {errors.username || ''}
                                        </p>
                                    </div>


                                    {/* Password Field */}
                                    <label className="block relative mt-1">
                                        <span className="text-sm text-gray-600">Password</span>
                                        <div className="mt-1 relative">
                                            <input
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                className={`block w-full rounded-md border px-3 py-2 shadow-sm placeholder-gray-400 pr-10 bg-white
                                                     focus:outline-none focus:ring-2 focus:ring-yellow-300
                                                     ${errors.password ? 'border-red-500 focus:ring-red-300' : 'border-gray-200'}`}
                                                placeholder="Enter password"
                                                required
                                                aria-invalid={!!errors.password}
                                            />
                                            <button
                                                type="button"
                                                onClick={togglePassword}
                                                onPointerDown={(e) => e.preventDefault()}
                                                aria-label={showPassword ? "Hide password" : "Show password"}
                                                title={showPassword ? "Hide password" : "Show password"}
                                                className="absolute inset-y-0 right-2 flex items-center px-2 focus:outline-none"
                                            >
                                                <Icon icon={showPassword ? 'mdi:eye' : 'mdi:eye-off'} className="text-lg" />
                                            </button>
                                        </div>
                                    </label>
                                    <div className="h-5 mt-1">
                                        <p
                                            className={`text-sm text-red-600 transition-opacity duration-200 ${errors.password ? 'opacity-100' : 'opacity-0'
                                                }`}
                                        >
                                            {errors.password || ''}
                                        </p>
                                    </div>


                                    {/* Forgot */}
                                    <div className="flex items-center justify-end mt-1">
                                        <span className="text-base text-[#002949] hover:underline cursor-pointer" 
                                         onClick={() => navigate("/forgot-password")}
                                         >
                                            Forgot?
                                        </span>
                                    </div>

                                    {/* Submit */}
                                    <button
                                        type="submit"
                                        disabled={authLoading}
                                        className="group mt-4 w-full inline-flex items-center justify-center rounded-xl px-4 py-2 
                      text-white shadow-xl bg-[#002949] border-2 border-blue-200 
                      font-bold text-base cursor-pointer transition-all duration-300 hover:bg-[#021e35] disabled:opacity-60"
                                    >
                                        {authLoading ? "Logging in..." : "Log in"}
                                        <div className="flex items-center px-2 text-white transition-transform duration-300 group-hover:translate-x-1">
                                            <Icon icon="flowbite:arrow-right-outline" className="font-bold text-lg" />
                                        </div>
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-black text-gray-200 text-center py-4 rounded-t-xl">
                <p className="text-sm">&copy; {new Date().getFullYear()} Kite Loan App. All rights reserved.</p>
            </footer>
        </div>
    );
};

export default Login;
